import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Button,
  IconButton,
  Chip,
  Typography,
  CircularProgress,
  Alert,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Popover,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  PersonAdd as PersonAddIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useOktaAuth } from '@okta/okta-react';
import useApi from '../../hooks/useApi';
import { useDebouncedSearch } from '../../hooks/useDebouncedSearch';
import { SearchTextField } from '../common';
import { PermissionGate } from '../Auth';
import { FONT_FAMILY_PRIMARY } from '../../constants/fonts';
import { getApiErrorMessage } from '../../utils/errorMessages';
import { useErrorToast } from '../common';

const UserList = () => {
  const api = useApi();
  const apiRef = useRef(api);
  const { authState } = useOktaAuth();
  const { showErrorToast } = useErrorToast();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { searchInput, setSearchInput, searchTerm, clearSearch } = useDebouncedSearch();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalUsers, setTotalUsers] = useState(0);
  const initialLoadDone = useRef(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  useEffect(() => {
    if (error) {
      showErrorToast(error);
      setError(null);
    }
  }, [error, showErrorToast]);
  const [userToDelete, setUserToDelete] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [allRoles, setAllRoles] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [loadingTenants, setLoadingTenants] = useState(false);
  const [creatingUser, setCreatingUser] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    fullName: '',
    tenantId: '',
    roleId: '',
  });
  const [formErrors, setFormErrors] = useState({});
  
  const [clientsAnchorEl, setClientsAnchorEl] = useState(null);
  const [selectedUserClients, setSelectedUserClients] = useState([]);
  const [rolesAnchorEl, setRolesAnchorEl] = useState(null);
  const [selectedUserRoles, setSelectedUserRoles] = useState([]);

  useEffect(() => {
    apiRef.current = api;
  }, [api]);

  const currentUserEmail = useMemo(() => {
    const claims = authState?.idToken?.claims || {};
    const accessClaims = authState?.accessToken?.claims || {};
    return claims.email || 
           claims.preferred_username ||  
           accessClaims.email || 
           accessClaims.sub || 
           claims.sub || 
           null;
  }, [authState]);

  const isCurrentUser = useCallback((user) => {
    if (!currentUserEmail || !user?.email) return false;
    const userEmail = user.email.toLowerCase().trim();
    const loggedInEmail = currentUserEmail.toLowerCase().trim();
    return userEmail === loggedInEmail;
  }, [currentUserEmail]);

  const loadUsers = useCallback(async (forceRefresh = false) => {
    if (!forceRefresh && initialLoadDone.current && loading) {
      return;
    }
    
    try {
      setLoading(true);
      setError(null);

      const params = {
        page: page + 1,
        pageSize: rowsPerPage,
      };
      if (searchTerm) {
        params.search = searchTerm;
      }

      const response = await apiRef.current.get('/users', { params });
      
      const apiResponse = response.data;

      console.log('User data sample:', apiResponse?.data?.users?.[0]);

      if (apiResponse && apiResponse.success && apiResponse.data) {
        setUsers(apiResponse.data.users || []);
        setTotalUsers(apiResponse.data.pagination?.totalItems || 0);
      } else if (apiResponse && apiResponse.users) {
        setUsers(apiResponse.users || []);
        setTotalUsers(apiResponse.pagination?.totalItems || 0);
      } else {
        console.warn('Unexpected API response structure:', apiResponse);
        setUsers([]);
        setTotalUsers(0);
      }
      
      initialLoadDone.current = true;
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to load users'));
      console.error('Error loading users:', err);
      setUsers([]);
      setTotalUsers(0);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, searchTerm, loading]);

  useEffect(() => {
    loadUsers();
  }, [page, rowsPerPage, searchTerm]);

  useEffect(() => {
    setPage(0);
  }, [searchTerm]);

  const loadRoles = useCallback(async () => {
    try {
      setLoadingRoles(true);
      const response = await apiRef.current.get('/users/roles');
      const apiResponse = response.data;
      
      if (apiResponse?.success && apiResponse?.data?.roles) {
        setAllRoles(apiResponse.data.roles);
      } else if (apiResponse?.roles) {
        setAllRoles(apiResponse.roles);
      } else {
        setAllRoles([
          { roleId: 1, roleName: 'JLL Super Admin', roleCode: 'JLL_SUPER_ADMIN' },
          { roleId: 2, roleName: 'JLL Analyst', roleCode: 'JLL_ANALYST' },
          { roleId: 3, roleName: 'Client Admin', roleCode: 'CLIENT_ADMIN' },
          { roleId: 4, roleName: 'Client Power User', roleCode: 'CLIENT_POWER_USER' },
          { roleId: 5, roleName: 'Client Viewer', roleCode: 'CLIENT_VIEWER' },
        ]);
      }
    } catch (err) {
      console.error('Error loading roles:', err);
      setAllRoles([]);
    } finally {
      setLoadingRoles(false);
    }
  }, []);

  const loadTenants = useCallback(async () => {
    try {
      setLoadingTenants(true);
      const response = await apiRef.current.get('/tenants', {
        params: { page: 1, pageSize: 500 },
      });
      const apiResponse = response.data;

      if (apiResponse?.success && apiResponse?.data?.tenants) {
        setTenants(apiResponse.data.tenants);
      } else {
        setTenants([]);
      }
    } catch (err) {
      console.error('Error loading tenants:', err);
      setTenants([]);
    } finally {
      setLoadingTenants(false);
    }
  }, []);

  const filteredRoles = useMemo(() => {
    if (!formData.tenantId) {
      return allRoles;
    }

    const selectedTenant = tenants.find(
      (t) => String(t.TenantId) === String(formData.tenantId)
    );
    if (!selectedTenant) {
      return allRoles;
    }

    const isJLLTenant = selectedTenant.TenantType === 'JLL';

    return allRoles.filter((role) => {
      const roleCode = (role.roleCode || role.RoleCode || '').toUpperCase();
      const isJLLRole = roleCode.startsWith('JLL_');
      return isJLLTenant ? isJLLRole : !isJLLRole;
    });
  }, [formData.tenantId, allRoles, tenants]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDeleteClick = (user) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!userToDelete) return;

    try {
      setLoading(true);
      await apiRef.current.delete(`/users/${userToDelete.userId}`);
      setSnackbar({ 
        open: true, 
        message: `User "${userToDelete.fullName || userToDelete.email}" has been deleted successfully.`, 
        severity: 'success' 
      });
      setDeleteDialogOpen(false);
      setUserToDelete(null);
      loadUsers(true);
    } catch (err) {
      const errorMessage = getApiErrorMessage(err, 'Failed to delete user');
      showErrorToast(errorMessage);
      setLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setUserToDelete(null);
  };

  const handleCreateUserClick = () => {
    setFormData({ email: '', fullName: '', tenantId: '', roleId: '' });
    setFormErrors({});
    loadRoles();
    loadTenants();
    setCreateDialogOpen(true);
  };

  const handleCreateDialogClose = () => {
    setCreateDialogOpen(false);
    setFormData({ email: '', fullName: '', tenantId: '', roleId: '' });
    setFormErrors({});
  };

  const handleFormChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => {
      const next = { ...prev, [name]: value };
      if (name === 'tenantId') {
        next.roleId = '';
      }
      return next;
    });
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    if (!formData.fullName) {
      newErrors.fullName = 'Full name is required';
    } else if (formData.fullName.length < 2) {
      newErrors.fullName = 'Full name must be at least 2 characters';
    }

    setFormErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCreateUserSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setCreatingUser(true);
      const createData = {
        email: formData.email,
        fullName: formData.fullName,
      };
      if (formData.tenantId) {
        createData.tenantId = formData.tenantId;
      }
      if (formData.roleId) {
        createData.roleId = formData.roleId;
      }
      
      await apiRef.current.post('/users', createData);
      
      setSnackbar({ 
        open: true, 
        message: `User "${formData.fullName}" created successfully!`, 
        severity: 'success' 
      });
      handleCreateDialogClose();
      loadUsers(true);
    } catch (err) {
      const errorMessage = getApiErrorMessage(err, 'Failed to create user');
      showErrorToast(errorMessage);
    } finally {
      setCreatingUser(false);
    }
  };

  const handleShowClients = (event, user) => {
    const clientsArray = user.assignedClients ? user.assignedClients.split(', ').filter(c => c) : [];
    setSelectedUserClients(clientsArray);
    setClientsAnchorEl(event.currentTarget);
  };

  const handleShowRoles = (event, user) => {
    const rolesArray = user.allRoles ? user.allRoles.split(', ').filter(r => r) : [];
    setSelectedUserRoles(rolesArray);
    setRolesAnchorEl(event.currentTarget);
  };

  const handleCloseClientsPopover = () => {
    setClientsAnchorEl(null);
    setSelectedUserClients([]);
  };

  const handleCloseRolesPopover = () => {
    setRolesAnchorEl(null);
    setSelectedUserRoles([]);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return 'N/A';
    }
  };

  const clientsPopoverOpen = Boolean(clientsAnchorEl);
  const rolesPopoverOpen = Boolean(rolesAnchorEl);

  if (loading && users.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography
          variant="h4"
          component="h1"
          sx={{
            fontFamily: FONT_FAMILY_PRIMARY,
            fontWeight: 600,
          }}
        >
          User Management
        </Typography>
        <PermissionGate permission="manage_users">
          <Button
            variant="contained"
            startIcon={<PersonAddIcon />}
            onClick={handleCreateUserClick}
            sx={{ backgroundColor: '#000', '&:hover': { backgroundColor: '#333' } }}
          >
            Add User
          </Button>
        </PermissionGate>
      </Box>

      <Box mb={3}>
        <SearchTextField
          placeholder="Search users by name or email..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onClear={clearSearch}
          aria-label="Search users"
        />
      </Box>

      <Paper elevation={1} sx={{ borderRadius: '8px' }}>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 600 }}>Full Name</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Roles</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Assigned Clients</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Added By</TableCell>
                <TableCell sx={{ fontWeight: 600, display: { xs: 'none', md: 'table-cell' } }}>Created Date</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                    <Typography variant="body1" color="text.secondary">
                      {searchTerm ? 'No users found matching your search.' : 'No users found.'}
                    </Typography>
                    <PermissionGate permission="manage_users">
                      <Button
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={handleCreateUserClick}
                        sx={{ mt: 2 }}
                      >
                        Add User
                      </Button>
                    </PermissionGate>
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user) => (
                  <TableRow key={user.userId} hover>
                    <TableCell>
                      <Box>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="body2" fontWeight={500}>
                            {user.fullName || user.displayName || 'N/A'}
                          </Typography>
                          {isCurrentUser(user) && (
                            <Chip 
                              label="You" 
                              size="small" 
                              sx={{ height: 18, fontSize: '0.7rem' }} 
                              color="secondary" 
                            />
                          )}
                        </Box>
                        {user.isJLLUser && (
                          <Chip label="JLL" size="small" sx={{ mt: 0.5, height: 18, fontSize: '0.7rem' }} color="primary" />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {user.email || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {user.allRoles ? (
                        <Box>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxWidth: '15rem' }}>
                            {user.allRoles.split(', ').slice(0, 2).map((role, idx) => (
                              <Chip
                                key={idx}
                                label={role}
                                color="default"
                                variant="outlined"
                                size="small"
                              />
                            ))}
                          </Box>
                          {user.allRoles.split(', ').length > 2 && (
                            <Button
                              size="small"
                              onClick={(e) => handleShowRoles(e, user)}
                              sx={{ 
                                mt: 0.5, 
                                p: 0, 
                                minWidth: 'auto', 
                                textTransform: 'none',
                                color: '#1976d2',
                                '&:hover': {
                                  backgroundColor: 'transparent',
                                  textDecoration: 'underline',
                                  color: '#1565c0'
                                }
                              }}
                            >
                              Show More ({user.allRoles.split(', ').length})
                            </Button>
                          )}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No roles
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {user.tenantCount > 0 ? (
                        <Box>
                          <Typography variant="body2" sx={{ 
                            maxWidth: '12rem', 
                            overflow: 'hidden', 
                            textOverflow: 'ellipsis', 
                            whiteSpace: 'nowrap' 
                          }}>
                            {user.tenantNames || user.assignedClients || 'N/A'}
                          </Typography>
                          {user.tenantCount > 1 && (
                            <Button
                              size="small"
                              onClick={(e) => handleShowClients(e, user)}
                              sx={{ 
                                mt: 0.5, 
                                p: 0, 
                                minWidth: 'auto', 
                                textTransform: 'none',
                                color: '#1976d2',
                                '&:hover': {
                                  backgroundColor: 'transparent',
                                  textDecoration: 'underline',
                                  color: '#1565c0'
                                }
                              }}
                            >
                              Show More ({user.tenantCount})
                            </Button>
                          )}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No clients
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {user.createdBy || 'System'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                      <Typography variant="body2" color="text.secondary">
                        {formatDate(user.createdDate)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <PermissionGate permission="manage_users">
                        <Tooltip title={isCurrentUser(user) ? "Cannot delete yourself" : "Delete User"}>
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteClick(user)}
                              color="error"
                              disabled={!user.isActive || isCurrentUser(user)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </PermissionGate>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={totalUsers}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {loading && users.length > 0 && (
        <Box
          position="fixed"
          top={0}
          left={0}
          right={0}
          bottom={0}
          display="flex"
          alignItems="center"
          justifyContent="center"
          bgcolor="rgba(255, 255, 255, 0.7)"
          zIndex={9999}
        >
          <CircularProgress />
        </Box>
      )}

      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          Confirm Delete User
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete <strong>{userToDelete?.fullName || userToDelete?.email}</strong>?
            <br /><br />
            This will soft delete the user (set as inactive) and remove them from all assigned tenants. 
            The user will no longer be able to access the system.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained" autoFocus>
            Delete User
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={createDialogOpen}
        onClose={handleCreateDialogClose}
        maxWidth="sm"
        fullWidth
        aria-labelledby="create-user-dialog-title"
      >
        <DialogTitle id="create-user-dialog-title">
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Create New User</Typography>
            <IconButton onClick={handleCreateDialogClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleFormChange}
                error={!!formErrors.email}
                helperText={formErrors.email}
                required
                autoFocus
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Full Name"
                name="fullName"
                value={formData.fullName}
                onChange={handleFormChange}
                error={!!formErrors.fullName}
                helperText={formErrors.fullName}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Client (Optional)</InputLabel>
                <Select
                  name="tenantId"
                  value={formData.tenantId}
                  onChange={handleFormChange}
                  label="Client (Optional)"
                  disabled={loadingTenants}
                >
                  <MenuItem value="">
                    <em>None - Assign client later</em>
                  </MenuItem>
                  {tenants.map((tenant) => (
                    <MenuItem key={tenant.TenantId} value={tenant.TenantId}>
                      {tenant.TenantName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Role (Optional)</InputLabel>
                <Select
                  name="roleId"
                  value={formData.roleId}
                  onChange={handleFormChange}
                  label="Role (Optional)"
                  disabled={loadingRoles}
                >
                  <MenuItem value="">
                    <em>None - Assign role later</em>
                  </MenuItem>
                  {filteredRoles.map((role) => (
                    <MenuItem key={role.roleId || role.RoleId} value={role.roleId || role.RoleId}>
                      {role.roleName || role.RoleName}
                    </MenuItem>
                  ))}
                </Select>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1 }}>
                  {formData.tenantId
                    ? 'Roles shown match the selected client'
                    : 'Select a client to filter roles, or assign later from Client Management'}
                </Typography>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCreateDialogClose} color="inherit" disabled={creatingUser}>
            Cancel
          </Button>
          <Button 
            onClick={handleCreateUserSubmit} 
            variant="contained" 
            disabled={creatingUser}
            sx={{ backgroundColor: '#000', '&:hover': { backgroundColor: '#333' } }}
          >
            {creatingUser ? 'Creating...' : 'Create User'}
          </Button>
        </DialogActions>
      </Dialog>

      <Popover
        open={clientsPopoverOpen}
        anchorEl={clientsAnchorEl}
        onClose={handleCloseClientsPopover}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Box sx={{ p: 2, minWidth: 250 }}>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Assigned Clients
          </Typography>
          <List dense>
            {selectedUserClients.map((client, idx) => (
              <ListItem key={idx} sx={{ px: 0 }}>
                <ListItemText primary={client} />
              </ListItem>
            ))}
          </List>
        </Box>
      </Popover>

      <Popover
        open={rolesPopoverOpen}
        anchorEl={rolesAnchorEl}
        onClose={handleCloseRolesPopover}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Box sx={{ p: 2, minWidth: 250 }}>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            All Roles
          </Typography>
          <List dense>
            {selectedUserRoles.map((role, idx) => (
              <ListItem key={idx} sx={{ px: 0 }}>
                <Chip
                  label={role}
                  color="default"
                  variant="outlined"
                  size="small"
                />
              </ListItem>
            ))}
          </List>
        </Box>
      </Popover>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserList;
